<template>
    <div v-if="renderRows.length" class="event-list">
        <template v-for="row in renderRows">
            <div v-if="row.divider" :key="row.key" class="pinned-divider"></div>

            <b-row v-else :key="row.key" :class="eventClass(row.rowIndex, row.totalRows)" class="py-2" @click="collapseList">
                <b-col>
                    <b-icon v-if="eventIcon(row.event)" :icon="eventIcon(row.event)" :class="eventIconClass(row.event)"></b-icon>

                    <router-link
                        v-if="includeSymbol && getAssetByPublicId(row.event.target_public_id)"
                        :to="{ name: 'asset', params: { symbol: getAssetByPublicId(row.event.target_public_id).symbol } }"
                        class="security_symbol ml-2"
                    >{{ getAssetByPublicId(row.event.target_public_id).symbol }}</router-link>

                    <b-icon v-if="row.event.has_linked_trades" icon="link-45deg" class="text-info ml-2"></b-icon>

                    <span :class="{ tappable: selectable }" @click="selectEvent(row.event, $event)">
                        <span v-html="formatMessage(row.event)" class="ml-2"></span>

                        <span class="text-secondary ml-2">{{ formatTimeFromISO(row.event.timestamp) }}</span>
                    </span>
                </b-col>
            </b-row>
        </template>

        <div class="text-center pb-3" v-if="events.length < eventList.length || showMore">
            <b-link @click.prevent="toggleShowMore" class="text-white show-more-less" href="#">
                <span v-if="!showMore">Show more (+{{ eventList.length - eventsToShow }})</span>
                <span v-else>Show less</span>
            </b-link>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import formatter from '@/mixins/formatter';

export default {
    name: "EventList",
    
    props: {
        eventList: {
            type: Array,
            required: true
        },
        // pins the display window no longer holds; the in-window ones arrive in eventList
        pinnedEvents: {
            type: Array,
            default: () => []
        },
        includeSymbol: {
            type: Boolean,
            default: false
        },
        showAll: {
            type: Boolean,
            default: false
        },
        showPins: {
            type: Boolean,
            default: false
        }
    },
    mixins: [formatter],
    data() {
        return {
            showMore: false,
            minEventsToShow: 5,
            eventStyles: {
                asset: {
                    exit: { icon: 'box-arrow-up', class: 'text-success' },
                    bonus_exit: { icon: 'award', class: 'text-warning' },
                    reinvest: { icon: 'arrow-down-up', class: 'text-warning' },
                    buy: { icon: 'box-arrow-in-down', class: 'text-danger' },
                    conviction: { icon: 'file-diff', class: 'text-info' },
                    disabled: { icon: 'trash', class: 'text-danger' },
                    enabled: { icon: 'arrow-counterclockwise', class: 'text-success' }
                }
            },
            pinnedStyle: { icon: 'star-fill', class: 'text-warning' }
        }
    },
    methods: {
        eventClass(rowIndex, totalRows) {
            let returnList = [this.rowClass(rowIndex)];
            
            if (rowIndex === 0) returnList.push('pt-3');
            if (rowIndex === totalRows - 1) returnList.push('pb-3');
            if (this.showMore) returnList.push('tappable');

            return returnList
        },
        selectEvent(event, clickEvent) {
            if (!this.selectable) return;

            // the rest of the row collapses the list, the text must not do both
            clickEvent.stopPropagation();

            this.$emit('select-event', event);
        },
        collapseList() {
            if (!this.showMore) return;

            this.showMore = false;
            this.scrollListIntoView();
        },
        scrollListIntoView() {
            this.$nextTick(() => {
                // the asset page navbar is fixed, so it would cover the first rows
                const navbar = document.getElementById('asset-navbar');
                const listTop = this.$el.getBoundingClientRect().top + window.scrollY - (navbar ? navbar.offsetHeight : 0);

                if (window.scrollY <= listTop) return;

                window.scrollTo({ top: listTop });
            });
        },
        eventStyle(event) {
            if (this.showPins && event.pinned) return this.pinnedStyle;

            if (event.type in this.eventStyles && event.subtype in this.eventStyles[event.type]) {
                return this.eventStyles[event.type][event.subtype];
            }

            return null
        },
        eventIcon(event) {
            const style = this.eventStyle(event);

            return style ? style.icon : null
        },
        eventIconClass(event) {
            const style = this.eventStyle(event);

            return style ? style.class : null
        },
        pricesToReplace(event) {
            const fields = ['buy_price', 'buy_size', 'current_price', 'target_price', 'exit_size'];
            const values = [];
            for (const field of fields) {
                if (event.data && field in event.data) {
                    values.push(event.data[field]);
                }
            }
            return values;
        },
        replacePricesAndReturnMessage(event, message) {
            const prices = this.pricesToReplace(event);
            if (!message) {
                message = event.message || '';
            }
            if (prices.length === 0) return message;

            // Strip trailing ".0"/".00" from numbers in the message first, so the
            // number-boundary match below isn't tripped up by backend-emitted
            // "75.0" — JS prices arrive as 75 (float→Number drops the .0), and
            // a trailing dot would otherwise reject the match as a partial number.
            message = message.replace(/(\d)\.00(?=[^0-9]|$)/g, '$1').replace(/(\d)\.0(?=[^0-9]|$)/g, '$1');

            // "message": "Exited $150 (3) at price/target 108663"
            // Match each price only when it stands alone as a number — i.e. not
            // adjacent to another digit or a decimal point. This prevents e.g.
            // buy_price=75 from matching inside "$750", or 82 from matching
            // inside "82.5", regardless of replacement order.
            prices.forEach((price) => {
                const escaped = `${price}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp(`(^|[^\\d.])${escaped}(?![\\d.])`);
                message = message.replace(re, (_match, prefix) => prefix + this.formatPriceAndMakeItMonospaced(price));
            });

            return message;
        },
        formatPriceAndMakeItMonospaced(price) {
            if (price === null || price === undefined) return '';
            return `<span class="monospaced slightly-smaller">${this.formatPrice(price)}</span>`;
        },
        // values land in element text only, never in an attribute, so quotes need no escape
        escapeHtml(text) {
            return `${text}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        },
        formatMessage(event) {
            let returnString = ''

            if (event.type === 'asset') {
                if (event.subtype === 'buy') {
                    returnString += this.escapeHtml(event.message);
                } else if (event.subtype === 'reinvest') {
                    returnString += `Reinvested ${event.data.reinvested_pids.length} at ${event.data.target_price}`;
                } else if (event.subtype === 'exit') {
                    returnString += `Exit $${event.data.exit_size} (${event.data.exit_pids.length}) at ${event.data.target_price}`;
                } else if (event.subtype === 'conviction') {
                    returnString += `Conviction <code>${this.escapeHtml(this.convictionString(event.data.old_conviction))}</code> &#x21E2; <code>${this.escapeHtml(this.convictionString(event.data.conviction))}</code>`;
                    
                    if (event.data.current_price) returnString += ` @ ${event.data.current_price}`;
                    if (event.data.lowest_open) {
                        let percentFromLowest = ((event.data.current_price/event.data.lowest_open -1)*100).toFixed(2)

                        let strPercentFromLowest = `${percentFromLowest > 0?'+':''}${this.formatPriceAndMakeItMonospaced(percentFromLowest)}%`

                        returnString += ` (${strPercentFromLowest} from lowest open)`;
                    }
                } else if (event.subtype === 'disabled') {
                    const holdingsNote = event.data.holdings_shares
                        ? `, total holdings ${this.formatHoldings(event.data.holdings_shares)} valued at $${event.data.holdings_value}`
                        : '';

                    returnString += `Asset disabled at $${event.data.exit_price} - deleted ${event.data.position_count} positions worth $${event.data.value_released}${holdingsNote}.`;
                } else if (event.subtype === 'enabled') {
                    const restoredNote = event.data.reopened_count
                        ? ` Restored ${event.data.reopened_count} positions worth $${event.data.reopened_value}`
                        : '';

                    returnString += `Re-enabled asset.${restoredNote}`;
                }
                else returnString += this.escapeHtml(event.message || '');
            }
            else returnString += this.escapeHtml(event.message || '');

            return this.replacePricesAndReturnMessage(event, returnString);
        },
        toggleShowMore() {
            this.showMore = !this.showMore;
            if (this.showMore) return;

            this.scrollListIntoView();
        },
        convictionString(conviction) {
            if (!conviction) return 'Regular'
            return conviction.charAt(0).toUpperCase() + conviction.slice(1)
        }
    },
    computed: {
        ...mapGetters({
            getAssetByPublicId: 'getAssetByPublicId'
        }),
        // pinned rows first, then the window in its own order; a pin shows twice
        pinnedRows() {
            if (!this.showPins) return [];

            const listed = this.eventList.filter(event => event.pinned);
            const listedIds = new Set(listed.map(event => event.public_id));
            // the API serves an in-window pin in both keys; the list object wins, so
            // a pin toggled in the modal moves the block row and the list row together
            const older = this.pinnedEvents.filter(event => event.pinned && !listedIds.has(event.public_id));

            return [...listed, ...older].sort((a, b) => this.dateFromISO(b.timestamp) - this.dateFromISO(a.timestamp));
        },
        renderRows() {
            const describe = (prefix, list) => list.map((event, index) => ({
                key: `${prefix}${event.public_id}`,
                event,
                rowIndex: index,
                totalRows: list.length
            }));

            const pinned = describe('pinned-', this.pinnedRows);
            const listed = describe('', this.events);

            if (!pinned.length) return listed;
            if (!listed.length) return pinned;

            return [...pinned, { key: 'pinned-divider', divider: true }, ...listed]
        },
        events() {
            if (this.showAll || this.showMore) {
                return this.eventList;
            }
    
            if (this.eventList.length > this.eventsToShow) return this.eventList.slice(0, this.eventsToShow);
            else return this.eventList;
        },
        eventsToShow() {
            return ((this.eventsToday > this.minEventsToShow) ? this.eventsToday  : this.minEventsToShow);
        },
        selectable() {
            return !!this.$listeners['select-event'];
        },
        eventsToday() {
            let events = 0
            this.eventList.forEach((event) => {
                if (event.timestamp.split('T')[0] === (new Date).toISOString().split('T')[0]) events += 1;
            });
            return events
        }
    }
}
</script>

<style scoped>

a.show-more-less { text-decoration: none; }

.pinned-divider {
    border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.tappable {
    cursor: pointer;
}

.slightly-smaller {
    font-size: 0.85rem;
}

</style>